import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Avatar,
  useColorModeValue,
  IconButton,
} from '@chakra-ui/react';
import { Notification } from '../../types';
import { format } from 'date-fns';
import { CheckIcon, DeleteIcon } from '@chakra-ui/icons';
import { Timestamp } from 'firebase/firestore';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: () => void;
  onDelete: () => void;
  onClick: () => void;
}

/**
 * Notification Item Component
 * Displays a single notification with actions
 */
export function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  onClick,
}: NotificationItemProps) {
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headingColor = useColorModeValue('gray.900', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const unreadBg = useColorModeValue('blue.50', 'blue.900');

  const readValue = notification.read;
  const isRead = readValue === true || (readValue instanceof Timestamp);
  const isReadTimestamp = readValue instanceof Timestamp;

  const formatDate = (timestamp: Timestamp | Date | string | undefined) => {
    if (!timestamp) return 'Unknown';
    if (timestamp instanceof Date) {
      return format(timestamp, 'MMM d, yyyy h:mm a');
    }
    if (timestamp instanceof Timestamp) {
      return format(timestamp.toDate(), 'MMM d, yyyy h:mm a');
    }
    return format(new Date(timestamp), 'MMM d, yyyy h:mm a');
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'rock_created':
      case 'rock_updated':
      case 'rock_completed':
        return 'blue';
      case 'issue_created':
      case 'issue_resolved':
        return 'orange';
      case 'action_item_created':
      case 'action_item_completed':
        return 'green';
      case 'metric_created':
      case 'metric_off_track':
        return 'purple';
      case 'wins_excited_created':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  return (
    <Box
      bg={isRead ? bgColor : unreadBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      p={4}
      cursor="pointer"
      _hover={{ shadow: 'md' }}
      onClick={onClick}
    >
      <HStack spacing={3} align="start">
        {notification.user && (
          <Avatar
            size="sm"
            src={notification.user.photoURL}
            name={notification.user.displayName}
          />
        )}
        <VStack align="stretch" flex={1} spacing={1}>
          <HStack justify="space-between">
            <HStack spacing={2}>
              <Text fontWeight="medium" color={headingColor}>
                {notification.notification || notification.title || 'Notification'}
              </Text>
              {notification.type && (
                <Badge colorScheme={getTypeColor(notification.type)} size="sm">
                  {notification.type.replace('_', ' ')}
                </Badge>
              )}
              {!isRead && (
                <Badge colorScheme="blue" size="sm">
                  New
                </Badge>
              )}
            </HStack>
            <HStack spacing={1}>
              {!isRead && (
                <IconButton
                  aria-label="Mark as read"
                  icon={<CheckIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkAsRead();
                  }}
                />
              )}
              <IconButton
                aria-label="Delete"
                icon={<DeleteIcon />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              />
            </HStack>
          </HStack>
          <Text fontSize="sm" color={textColor}>
            {notification.message}
          </Text>
          <Text fontSize="xs" color={textColor}>
            {formatDate(notification.timestamp || notification.createdAt)}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
}

